import {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { BackLink } from 'enrollment/components';
import { Loader, PageNotFound } from 'theSource/components';
import {
  useEnrollmentStatusQuery,
  useLifeEventQuery,
} from 'enrollment/queries';
import {
  useConfigurationQuery,
  useCompanySettingsQuery,
  GET_ENROLLMENT_CONTEXT_QUERY_KEY,
} from 'theSource/queries';
import { usePageContentQuery } from 'enrollment/queries/usePageContentQuery';
import { queryClient, useApiMutation } from 'theSource/utils';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
import { CreateLifeEventRequestDto } from './types';
import { setEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { setEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { selectSubscriberDetails } from '../Profile/services';
import { ROUTES } from 'enrollment/app/routes';
import { ROUTES as SOURCE_ROUTES } from 'theSource/app/routes';
import { PsButtonTuner } from 'stencilSetup';
import { ThemedHeader } from 'components/ThemedHeader';
import './scss/ReportLifeEvent.css';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { ToggleBooleanState } from 'components/ToggleBooleanState';
import { SanitizedPageContent } from 'enrollment/components/PageContentDetails/SanitizedContent';
import { FormElement } from 'enrollment/components/Shared/FormElement';
import {
  isValidLifeEventDate,
  getMaxDateFromToday,
  getMinDateFromToday,
} from './utils';

interface ReportLifeEventState {
  state: {
    selected: number;
  };
}

declare const bootstrap: any;

export const ReportLifeEvent = () => {
  const { t } = useTranslation('pages.reportLifeEvent');
  const location = useLocation() as ReportLifeEventState;
  const dispatch = useDispatch();

  const [selectedLifeEventId, setSelectedLifeEventId] = useState(
    location?.state?.selected || ''
  );
  const [showTermReason, setShowTermReason] = ToggleBooleanState(false) as [
    boolean,
    (value?: boolean) => void,
  ];

  const [termReasonValue, setTermReasonValue] = useState('');

  const [terminationReasonId, setTerminationReasonId] = useState<
    number | undefined
  >(undefined);

  const [lifeEventDate, setLifeEventDate] = useState<Date | string | null>(
    null
  );
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [dateError, setDateError] = useState('');
  const navigate = useNavigate();

  const subscriberData = useSelector(selectSubscriberDetails);
  const profileAvatar = subscriberData?.subscriber_profile?.avatar;
  const profilePicture = subscriberData?.subscriber_profile?.profile_picture;

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const lumosEnabled = companySettings?.data.lumos_feature_toggle;
  const apiHost = configuration?.apiHost;

  const { data, isLoading, isFetching } = useLifeEventQuery({
    enabled: true,
  });

  const { pageContent, isFetchingPageContent, isLoadingPageContent } =
    usePageContentQuery({ pageContentLookupCode: 'sub/enroll_life_event' });

  const { EnrollmentStatusRequest } = useEnrollmentStatusQuery({
    lumosApiHost: String(apiHost),
  });

  const [lifeEventsBtnDisabled, setLifeEventsBtnDisabled] = ToggleBooleanState(
    true
  ) as [boolean, (value?: boolean) => void];

  const { isLoading: isSavingLifeEvent, mutate: createLifeEventAndContinue } =
    useApiMutation<ResponseDto, ErrorResponseDto, CreateLifeEventRequestDto>({
      queryKey: 'create-life-event',
      method: 'post',
      endpoint: `${apiHost}/v1/self_service/life_events`,
      queryKeysToInvalidate: GET_ENROLLMENT_CONTEXT_QUERY_KEY,
      onSuccess: () => {
        queryClient.invalidateQueries('init-benefits-life_event');
        dispatch(setEnrollmentContext('life_event'));
        dispatch(setEnrollmentLatest('profile'));
        EnrollmentStatusRequest({
          enrollment_type: 'life_event',
          status: 'profile',
        });
        if (Boolean(profilePicture) || Boolean(profileAvatar)) {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.profile.path}`
          );
        } else {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.profileEdit.path}`
          );
        }
      },
      showSuccessSnackbar: false,
      getErrorMessage: (error) => error.response?.data?.details,
    });

  useEffect(() => {
    // Find the selected life event
    const lifeEvent = data?.data.org_life_events.find(
      (lifeEvent) =>
        Number(lifeEvent.org_life_event_id) === Number(selectedLifeEventId)
    );

    // Initial state: Disable the button if no life event or date is selected
    if (!lifeEvent || !lifeEventDate) {
      setLifeEventsBtnDisabled(true);
      setDateError('');
      return;
    }

    // Validate the life event date
    if (
      isValidLifeEventDate(
        lifeEvent,
        lifeEventDate instanceof Date
          ? lifeEventDate.toISOString()
          : lifeEventDate
      )
    ) {
      // Check if termination reason is required and valid
      if (showTermReason && termReasonValue.length === 0) {
        setLifeEventsBtnDisabled(true);
      } else {
        setLifeEventsBtnDisabled(false);
      }
      setDateError('');
    } else {
      // Set error message for invalid date
      const lifeEventMax = getMaxDateFromToday(lifeEvent.max_days_before_event);
      const lifeEventMin = getMinDateFromToday(lifeEvent.max_days_after_event);
      const formattedLifeEventMax = moment(lifeEventMax).format('MM/DD/YYYY');
      const formattedLifeEventMin = moment(lifeEventMin).format('MM/DD/YYYY');

      setDateError(
        `Invalid Date: Must be after ${formattedLifeEventMin} and before ${formattedLifeEventMax}`
      );
      setLifeEventsBtnDisabled(true);
    }
  }, [
    lifeEventDate,
    selectedLifeEventId,
    termReasonValue,
    showTermReason,
    data?.data.org_life_events,
    setLifeEventsBtnDisabled,
  ]);

  if (
    isLoading ||
    isFetching ||
    isFetchingPageContent ||
    isLoadingPageContent ||
    isLoadingConfiguration
  ) {
    return <Loader />;
  }

  if (companySettings && !lumosEnabled) {
    return <PageNotFound isFullScreen={true}></PageNotFound>;
  }

  const orgLifeEvents = data?.data.org_life_events;

  const handleLifeEventChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedLifeEventId(Number(event.target.value));
  };

  const handleDateChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setLifeEventDate((e.target as HTMLInputElement).value);
  };

  const handleTermReasonChange = (e: FormEvent<HTMLPsSelectTunerElement>) => {
    const target = e.target as HTMLSelectElement;
    setTerminationReasonId(Number(target.value));

    if (lifeEventDate !== null && lifeEventDate !== '') {
      setLifeEventsBtnDisabled(false);
    }

    setTermReasonValue(target.value);
  };

  const handleNotesUpdate = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    setNotes((event.target as HTMLTextAreaElement).value);
  };

  const handleLifeEventChoice = (e: any) => {
    const collapseElement = document.querySelector('#collapseInfo');
    const collapseInfo = new bootstrap.Collapse(collapseElement, {
      toggle: false,
    });

    const value = e.target.value;

    if (value.length === 0) {
      collapseInfo.hide();
    } else {
      handleLifeEventChange(e);
      collapseInfo.show();
    }

    // setLifeEventDate
    if (typeof lifeEventDate === 'string') {
      setLifeEventDate('');
      setLifeEventsBtnDisabled(true);
    }

    const selectedLifeEvent = data?.data.org_life_events.find(
      (lifeEvent) => lifeEvent.org_life_event_id === Number(value)
    );
    const selectedLifeEventName = selectedLifeEvent?.name || '';

    const termReasonElement = document.querySelector('#termReason');
    const collapseTermReason = new bootstrap.Collapse(termReasonElement, {
      toggle: false,
    });

    if (selectedLifeEventName.toLowerCase().includes('admin')) {
      setShowTermReason(true);
      collapseTermReason.show();
    } else {
      setTermReasonValue('');
      setShowTermReason(false);
      collapseTermReason.hide();
    }
  };

  const handleContinueClick = () => {
    const formattedDateString = moment(lifeEventDate).format('YYYY-MM-DD');
    createLifeEventAndContinue({
      org_life_event_id: selectedLifeEventId as number,
      date: formattedDateString,
      termination_reason_id: showTermReason ? terminationReasonId : undefined,
      admin_override_id: showTermReason
        ? Number(selectedLifeEventId)
        : undefined,
      notes: notes,
    });
  };

  const filteredLifeEvents = orgLifeEvents;
  const pageDetails = pageContent?.data.page_text?.replace(/<[^>]*>?/gm, '');
  const description = filteredLifeEvents?.find(
    (event) => event.org_life_event_id === selectedLifeEventId
  )?.description;

  // Find the selected life event and extract min/max days
  const selectedLifeEvent = data?.data.org_life_events.find(
    (lifeEvent) =>
      Number(lifeEvent.org_life_event_id) === Number(selectedLifeEventId)
  );
  const minDays = selectedLifeEvent?.max_days_before_event;
  const maxDays = selectedLifeEvent?.max_days_after_event;

  return (
    <div>
      <div className="row">
        <BackLink
          underline={false}
          testId="back-button"
          className="mb-5"
          defaultPath="/"
        />
      </div>
      <ThemedHeader cssClass="mb-5">{t('title')}</ThemedHeader>
      {pageContent?.data.page_text ? (
        <>
          <p className="mb-0">
            {expanded ? pageDetails : pageDetails?.substring(0, 300) + '...'}
          </p>
          <PsButtonTuner
            data-testid="learn-more-link"
            buttonId="learn-more-link"
            label={expanded ? t('showLess') : t('showMore')}
            rootClass="d-inline-block mb-4"
            extraClass="btn-custom"
            onClick={() => setExpanded(!expanded)}
          />
        </>
      ) : (
        <></>
      )}
      <div className="card report-life-event-card">
        <h3>{t('titleCard')}</h3>

        {/* Choose Life Event */}
        <div className="row">
          <div className="col col-md-5 col-lg-6 pr-lg-0">
            <FormElement
              inputId="choose-life-event"
              data-testid="choose-life-event"
              label={t('choose')}
              formField="select"
              options={filteredLifeEvents?.map((lifeEvent) => ({
                key: lifeEvent.org_life_event_id,
                value: lifeEvent.org_life_event_id.toString(),
                label: lifeEvent.name,
              }))}
              optionOne={t('optionOne')}
              onInput={(e: ChangeEvent<HTMLInputElement>) =>
                handleLifeEventChoice(e)
              }
            />
          </div>
        </div>

        <div
          className="collapse-info collapse"
          id="collapseInfo"
          data-testid="collapse-info"
        >
          <div className="row mt-5">
            <div className="col">
              <p className="semiBold">
                {
                  filteredLifeEvents?.find(
                    (event) => event.org_life_event_id === selectedLifeEventId
                  )?.name
                }
              </p>
              <SanitizedPageContent html={description || ''} classes="mb-4" />

              <PSBannerWrapper
                id="provide-documentation"
                level="info"
                icon
                message={t('info')}
              />
            </div>
          </div>

          {/* Termination Reason */}
          <div className="collapse" id="termReason">
            <div className="row mt-4">
              <div className="col col-md-5 col-lg-6 pr-lg-0">
                <FormElement
                  inputId="termination-reasons"
                  data-testid="termination-reasons"
                  label={t('terminationReason')}
                  formField="select"
                  required={true}
                  validationMessage="Required"
                  value={termReasonValue}
                  optionOne={t('selectTerminationReason')}
                  options={data?.data.termination_reasons?.map((reason) => ({
                    key: reason.id,
                    value: reason.id.toString(),
                    label: reason.name,
                  }))}
                  onInput={(e: FormEvent<HTMLPsSelectTunerElement>) =>
                    handleTermReasonChange(e)
                  }
                />
              </div>
            </div>
          </div>

          <p className="mt-5 mb-0">
            {t('moreDetails')} {t('common:fieldIsRequired')}(
            <span className="red">*</span>) {t('common:fieldIsRequiredTwo')}
          </p>

          {/* Life Event Date */}
          <div className="row">
            <div
              className="col col-md-5 col-lg-6 pr-lg-0"
              {...(minDays !== undefined && maxDays !== undefined
                ? { 'aria-describedby': 'date-range' }
                : {})}
            >
              <FormElement
                inputId="life-event-date"
                data-testid="life-event-date"
                name="life-event-date"
                formField="date"
                label={t('date')}
                required={true}
                extraClass="full-width-mobile full-width-tablet"
                validationMessage={Boolean(dateError)}
                value={lifeEventDate}
                onChange={(e: any) => handleDateChange(e)}
                min={(() => {
                  const lifeEvent = data?.data.org_life_events.find(
                    (lifeEvent) =>
                      Number(lifeEvent.org_life_event_id) ===
                      Number(selectedLifeEventId)
                  );
                  if (!lifeEvent) {
                    return undefined;
                  }
                  return moment()
                    .subtract(lifeEvent.max_days_before_event, 'days')
                    .format('YYYY-MM-DD');
                })()}
                max={(() => {
                  const lifeEvent = data?.data.org_life_events.find(
                    (lifeEvent) =>
                      Number(lifeEvent.org_life_event_id) ===
                      Number(selectedLifeEventId)
                  );
                  if (!lifeEvent) {
                    return undefined;
                  }
                  return moment()
                    .add(lifeEvent.max_days_after_event, 'days')
                    .format('YYYY-MM-DD');
                })()}
              />
            </div>
            {minDays !== undefined && maxDays !== undefined && (
              <div
                className="col-4 col-md-8 col-lg-12 helper-text"
                id="date-range"
              >
                {t('dateHelperOne')} {minDays} {t('dateHelperTwo')} {maxDays}{' '}
                {t('dateHelperThree')}
              </div>
            )}
          </div>

          {/* Life Events Notes */}
          <div className="row">
            <div className="col">
              <FormElement
                inputId="life-event-notes"
                formField="textarea"
                label={t('notes')}
                onKeyUp={handleNotesUpdate}
                rows={5}
              />
            </div>
          </div>
        </div>
      </div>

      {!isSavingLifeEvent ? (
        <div className="row">
          <div className="col text-center text-md-left">
            <PsButtonTuner
              buttonId="create-life-event"
              data-testid="create-life-event"
              level="primary"
              label={t('looksGood')}
              onClick={handleContinueClick}
              disabled={
                lifeEventsBtnDisabled ? lifeEventsBtnDisabled : undefined
              }
            />
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
};
